// src/components/job-engine/useDeadLetter.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getDeadLetter } from "@/api/jobEngine";
import type { DeadLetter } from "@/api/jobEngine";

export interface UseDeadLetterResult {
  data: DeadLetter | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDeadLetter(id: string): UseDeadLetterResult {
  const [data, setData] = useState<DeadLetter | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!id) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getDeadLetter(id, ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setData(r); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load dead letter");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => ctrl.abort();
  }, [id, tick]);

  return { data, isLoading, error, refetch };
}
